// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'
import type { LiteralUnion } from 'type-fest'

import type { Attributes, QdsFocusEventDetail } from '../../helpers'
import { inheritAriaAttributes, pickFocusEventAttributes } from '../../helpers'
import type { Importance, Size } from '../shared'

export type Action = 'dropdown-close' | 'dropdown' | 'next'
export type ButtonTarget = '_blank' | '_parent' | '_self' | '_top'
export type ButtonType = 'button' | 'reset' | 'submit'
export type FormMethod = 'dialog' | 'get' | 'post'
export type ButtonVariant = 'destructive' | 'hero'
export type PopupType = 'dialog' | 'grid' | 'menu' | 'tree' | 'true'

/**
 * The `<qds-button>` element is an interactive element activated by a user
 * with a mouse, keyboard, finger, voice command, or other assistive technology.
 * Once activated, it then performs a programmable action, such as submitting a
 * [form](https://developer.mozilla.org/docs/Learn/Forms)
 * or opening a dialog.
 *
 * @see https://quartz.se.com/build/components/button
 */
@Component({
  tag: 'qds-button',
  shadow: { delegatesFocus: true },
  styleUrl: 'button.css',
})
export class Button implements ComponentInterface {
  /**
   * The name of the action icon to render.
   */
  @Prop() public readonly action?: Action

  /**
   * The button's badge text.
   */
  @Prop() public readonly badge?: string

  /**
   * Adds accessible text to the badge that will be used by screen readers.
   *
   * @example
   * <qds-button badge="2" badge-description="new notifications"></qds-button>
   * // This button's badge will be read by screen readers as "2 new notifications"
   */
  @Prop() public readonly badgeDescription?: string

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * The button's importance.
   */
  @Prop() public readonly importance?: Importance = 'standard'

  /**
   * Indicates the type of interactive popup element that can be triggered by
   * the `qds-button`.
   */
  @Prop() public readonly popupType?: PopupType

  /**
   * The button's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * The button's text.
   */
  @Prop() public readonly text?: string

  /**
   * The button's variant.
   */
  @Prop() public readonly variant?: ButtonVariant

  /**
   * Prevents the button from being interacted with: it cannot be pressed or
   * focused.
   *
   * @webnative
   */
  @Prop()
  public readonly disabled?: boolean

  /**
   * Tells the browser to download the linked file as this filename. Only used
   * when `href` is set.
   *
   * @webnative
   */
  @Prop() public readonly download?: string

  /**
   * The [`<form>`](https://developer.mozilla.org/docs/Web/HTML/Element/form)
   * element to associate the button with (its form owner).
   *
   * The value of this attribute must be the id of a `<form>` in the same
   * document. If this attribute is not set, the `<button>` is associated with
   * its ancestor `<form>` element, if any.
   *
   * This attribute lets you associate `<button>` elements to `<form>`s
   * anywhere in the document, not just inside a `<form>`. It can also override
   * an ancestor `<form>` element.
   *
   * @webnative
   */
  @Prop() public readonly form?: string

  /**
   * The URL that processes the information submitted by the button. Overrides
   * the [`action`](https://developer.mozilla.org/docs/Web/HTML/Element/button#attr-formaction)
   * attribute of the button's form owner. Does nothing if there is no form owner.
   *
   * @webnative
   */
  @Prop({ attribute: 'formaction' }) public readonly formAction?: string

  /**
   * If the button is a submit button (it's inside/associated with a `<form>`
   * and doesn't have `type="button"`), this attribute specifies the
   * [HTTP method][] used to submit the form, or `dialog` which won't submit
   * the form. Possible values:
   *
   * - `get`: The form data are appended to the form's action URL, with a ? as
   * a separator, and the resulting URL is sent to the server. Use this method
   * when the form has no side effects, like search forms.
   * - `post`: The data from the form are included in the body of the HTTP
   * request when sent to the server. Use when the form contains information
   * that shouldn't be public, like login credentials.
   * - `dialog`: Close the dialog box in which the button finds itself, if any,
   * and do not submit the button's form owner.
   *
   * If specified, this attribute overrides the `method` attribute of the
   * button's form owner.
   *
   * [HTTP method]: https://developer.mozilla.org/docs/Web/HTTP/Methods
   *
   * @webnative
   */
  @Prop({ attribute: 'formmethod' }) public readonly formMethod?: FormMethod

  /**
   * If the button is a submit button, this attribute specifies that the form
   * is not to be
   * [validated](https://developer.mozilla.org/docs/Learn/Forms/Form_validation)
   * when it is submitted. If this attribute is specified, it overrides the
   * [`novalidate`](https://developer.mozilla.org/docs/Web/HTML/Element/form#attr-novalidate)
   * attribute of the button's form owner.
   *
   * @webnative
   */
  @Prop({ attribute: 'formnovalidate' })
  public readonly formNoValidate: boolean = false

  /**
   * If the button is a submit button, this attribute is an author-defined name
   * or standardized, underscore-prefixed keyword indicating where to display
   * the response from submitting the form. This is the `name` of, or keyword
   * for, a browsing context (a tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe)).
   * If this attribute is specified, it overrides the
   * [`target`](https://developer.mozilla.org/docs/Web/HTML/Element/form#attr-target)
   * attribute of the button's form owner. The following keywords have special
   * meanings:
   *
   * - `_blank`: Load the response into a new unnamed browsing context — usually
   * a new tab or window, depending on the user's browser settings.
   * - `_parent`: Load the response into the parent browsing context of the
   * current one. If there is no parent, this option behaves the same way as
   * `_self`.
   * - `_self`: Load the response into the same browsing context as the current
   * one.
   * - `_top`: Load the response into the top-level browsing context (that is,
   * the browsing context that is an ancestor of the current one, and has no
   * parent). If there is no parent, this option behaves the same way as
   * `_self`.
   *
   * @webnative
   */
  @Prop({ attribute: 'formtarget' })
  public readonly formTarget: LiteralUnion<ButtonTarget, string> = '_self'

  /**
   * When set, the underlying button will be rendered as an `<a>` with this
   * `href` instead of a `<button>`.
   *
   * @webnative
   */
  @Prop() public readonly href?: string

  /**
   * When href is define and the button act as a link (`<a>`), the `rel` will be added to the element.
   *
   * @webnative
   */
  @Prop() public readonly rel?: string = 'noopener'

  /**
   * The name of the button, submitted as a pair with the button's `value` as
   * part of the form data, when that button is used to submit the form.
   * Ignored when `href` is set.
   *
   * @webnative
   */
  @Prop() public readonly name?: string

  /**
   * Where to display the linked URL, as the name for a browsing context (a
   * tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe).
   * The following keywords have special meanings for where to load the URL:
   *
   * - `_blank`: usually a new tab, but users can configure browsers to open a new
   *   window instead.
   * - `_parent`: the parent browsing context of the current one. If no parent,
   *   behaves as `_self`.
   * - `_self`: the current browsing context.
   * - `_top`: the topmost browsing context (the "highest" context that's an
   *   ancestor of the current one). If no ancestors, behaves as `_self`.
   *
   * @webnative
   */
  @Prop() public readonly target?: LiteralUnion<ButtonTarget, string>

  /**
   * The type of button.
   *
   * `button`: The button has no default behavior, and does nothing when
   * pressed by default. It can have client-side scripts listen to the
   * element's events, which are triggered when the events occur.
   *
   * `submit`: The button submits the form data to the server. This is the
   * default if the attribute is not specified for buttons associated with a
   * `<form>`, or if the attribute is an empty or invalid value.
   *
   * `reset`: The button resets all the controls to their initial values.
   *
   * @webnative
   */
  @Prop() public readonly type?: ButtonType

  /**
   * Defines the value associated with the button's `name` when it's submitted
   * with the form data. This value is passed to the server in params when the
   * form is submitted using this button. Ignored when `href` is set.
   *
   * @webnative
   */
  @Prop() public readonly value?: string

  /**
   * Emitted when the button loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the button gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @Element() private readonly host!: HTMLQdsButtonElement

  @State() private tabIndex?: number

  #inheritedAttributes: Attributes = {}

  get #ariaExpanded(): 'false' | 'true' | undefined {
    switch (this.#computedAction) {
      case 'dropdown': {
        return 'false'
      }
      case 'dropdown-close': {
        return 'true'
      }
      default: {
        return undefined
      }
    }
  }

  get #ariaHaspopup(): PopupType | undefined {
    switch (this.popupType) {
      case 'dialog':
      case 'grid':
      case 'menu':
      case 'tree':
      case 'true': {
        return this.popupType
      }
      default: {
        return undefined
      }
    }
  }

  get #formElement(): HTMLElement | null {
    return this.form !== undefined && this.form !== ''
      ? document.querySelector(`#${this.form}`)
      : this.host.closest('form')
  }

  get #buttonType(): ButtonType {
    if (this.type === undefined) return this.#formElement ? 'submit' : 'button'
    return this.type
  }

  get #iconOnly(): boolean {
    const hasIcon = this.iconName !== undefined && this.iconName !== ''
    const hasNoLabel = this.text === undefined || this.text === ''

    return hasIcon && hasNoLabel
  }

  get #computedAction(): Action | undefined {
    switch (this.action) {
      case 'dropdown':
      case 'dropdown-close':
      case 'next': {
        return this.action
      }
      default: {
        return undefined
      }
    }
  }

  get #computedDisabled(): boolean {
    return this.disabled ?? false
  }

  get #computedImportance(): Importance | undefined {
    if (this.variant === 'destructive') return undefined

    switch (this.importance) {
      case 'emphasized':
      case 'subdued': {
        return this.importance
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedSize(): Size | undefined {
    if (this.variant === 'hero') return undefined

    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) {
      event.stopImmediatePropagation()
      return
    }

    if (this.#buttonType === 'button' || !this.#formElement) return
    event.preventDefault()

    const fakeButton = document.createElement('button')
    fakeButton.type = this.#buttonType
    if (this.name !== undefined) fakeButton.name = this.name
    if (this.value !== undefined) fakeButton.value = this.value
    if (this.formAction !== undefined) fakeButton.formAction = this.formAction
    if (this.formMethod !== undefined) fakeButton.formMethod = this.formMethod
    fakeButton.formNoValidate = this.formNoValidate
    fakeButton.formTarget = this.formTarget
    fakeButton.style.display = 'none'

    this.#formElement.append(fakeButton)
    fakeButton.click()
    fakeButton.remove()
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex =
      parsedValue === 0 || Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  public componentWillLoad(): void {
    this.#inheritedAttributes = inheritAriaAttributes(this.host)
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
  }

  public render() {
    const Tag = this.href === undefined ? 'button' : 'a'
    const ariaDisabled = this.#computedDisabled ? 'true' : undefined
    const disabledTabIndex = Tag === 'a' ? -1 : undefined

    return (
      <div class="qds-container">
        <Tag
          aria-disabled={Tag === 'a' ? ariaDisabled : undefined}
          class={{
            'qds-button': true,
            'qds-icon-only': this.#iconOnly,
          }}
          aria-expanded={this.#ariaExpanded}
          aria-haspopup={this.#ariaHaspopup}
          data-importance={this.#computedImportance}
          data-size={this.#computedSize}
          data-action={this.#computedAction}
          data-variant={this.variant}
          disabled={Tag === 'a' ? undefined : this.disabled}
          download={Tag === 'a' ? this.download : undefined}
          href={Tag === 'a' ? this.href : undefined}
          name={Tag === 'a' ? undefined : this.name}
          onBlur={this.#onBlur}
          onFocus={this.#onFocus}
          rel={Tag === 'a' ? this.rel : undefined}
          role={
            Tag === 'a' && this.download !== undefined ? 'button' : undefined
          }
          tabIndex={this.#computedDisabled ? disabledTabIndex : this.tabIndex}
          target={Tag === 'a' ? this.target : undefined}
          type={Tag === 'a' ? undefined : this.#buttonType}
          value={Tag === 'a' ? undefined : this.value}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.#inheritedAttributes}
        >
          {this.iconName !== undefined && this.iconName !== '' && (
            <qds-icon
              aria-hidden="true"
              class="qds-icon"
              data-size={this.#computedSize}
              library={this.iconLibrary}
              name={this.iconName}
            />
          )}
          {this.text}
          {this.badge !== undefined && this.badge !== '' && (
            <div
              class="qds-badge"
              data-importance={this.#computedImportance}
              data-variant={this.variant}
            >
              {this.badge}
              <span class="qds-sr-only">{this.badgeDescription}</span>
            </div>
          )}
          {this.#computedAction !== undefined && (
            <qds-icon
              aria-hidden="true"
              class="qds-icon qds-action"
              data-size={this.#computedSize}
              data-variant={this.variant}
              library="core"
              name={this.#computedAction}
            />
          )}
        </Tag>
        <div
          class={{
            'qds-focus-ring': true,
            'qds-icon-only': this.#iconOnly,
          }}
          data-importance={this.#computedImportance}
          data-size={this.#computedSize}
          data-variant={this.variant}
        />
      </div>
    )
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }
}
